import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { FileText, ShoppingCart, Truck, ClipboardList, PackageCheck, Plus, ArrowRight, Edit2, Trash2, ChevronRight, Printer } from "lucide-react";
import api, { formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { dispatchPrintOrder } from "@/lib/printDocuments";

const TYPES = [
  { key: "Quotation",     label: "Quotations",      icon: FileText,       color: "indigo" },
  { key: "Sales Order",   label: "Sales Orders",    icon: ShoppingCart,   color: "blue" },
  { key: "Delivery Note", label: "Delivery Notes",  icon: Truck,          color: "teal" },
  { key: "Purchase Order", label: "Purchase Orders", icon: ClipboardList, color: "amber" },
  { key: "Goods Receipt", label: "Goods Receipts",  icon: PackageCheck,   color: "emerald" },
];

const STATUS_BADGE = {
  Open:      "bg-blue-50 text-blue-700 border-blue-200",
  Converted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Cancelled: "bg-gray-100 text-gray-500 border-gray-200",
};

function fmt(v) {
  if (v === 0 || v == null) return "—";
  return `₹${formatINR(v)}`;
}

function Inner() {
  const { activeId, active } = useCompany();
  const [type, setType] = useState("Quotation");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    if (!activeId) return;
    let cancelled = false;
    api.get(`/companies/${activeId}/orders`, { params: { type } }).then((r) => {
      if (cancelled) return;
      setOrders(r.data || []);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [activeId, type, refresh]);

  const counts = useMemo(() => {
    const c = { Open: 0, Converted: 0, Cancelled: 0 };
    orders.forEach((o) => { if (c[o.status] != null) c[o.status]++; });
    return c;
  }, [orders]);

  const convertOrder = async (oid) => {
    try {
      const r = await api.post(`/companies/${activeId}/orders/${oid}/convert`);
      toast.success(`Converted to ${r.data.voucher_no}`);
      setRefresh((x) => x + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Conversion failed");
    }
  };

  const deleteOrder = async (oid) => {
    if (!window.confirm("Delete this order?")) return;
    try {
      await api.delete(`/companies/${activeId}/orders/${oid}`);
      toast.success("Order deleted");
      setRefresh((x) => x + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  /**
   * Print an order as a statutory document:
   *   - Quotation     → Proforma Invoice (non-tax quote)
   *   - Delivery Note → Delivery Challan (Rule 55 — movement of goods)
   *   - others        → fall back to Proforma layout
   *
   * Fetches the party ledger lazily so the printed doc shows the buyer's
   * address + GSTIN.
   */
  const onPrint = async (o) => {
    let partyLedger = null;
    if (o.party_ledger_id) {
      try {
        const { data } = await api.get(`/ledgers/${o.party_ledger_id}`);
        partyLedger = data;
      } catch (err) {
        // Ledger may have been deleted — print anyway using order snapshot.
      }
    }
    dispatchPrintOrder(o, { company: active || {}, partyLedger });
  };

  return (
    <>
      <PageHeader
        title="Orders & Documents"
        subtitle="Pre-accounting paper trail — Quotations, Sales Orders, Delivery Notes, Purchase Orders, GRN"
        actions={
          <Button onClick={() => nav(`/orders/new?type=${encodeURIComponent(type)}`)} data-testid="btn-new-order">
            <Plus className="w-4 h-4 mr-1" /> New {type}
          </Button>
        }
      />
      <div className="p-6 space-y-4" data-testid="orders-page">
        {/* Type filter chips */}
        <div className="flex flex-wrap gap-2">
          {TYPES.map((t) => {
            const Icon = t.icon;
            const active = type === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setType(t.key)}
                data-testid={`type-chip-${t.key.toLowerCase().replace(/ /g, "-")}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                  active
                    ? "bg-blue-700 text-white border-blue-700"
                    : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Status summary */}
        <div className="flex gap-3 text-xs">
          <span className="text-gray-500">Open: <span className="font-semibold text-blue-700">{counts.Open}</span></span>
          <span className="text-gray-500">Converted: <span className="font-semibold text-emerald-700">{counts.Converted}</span></span>
          <span className="text-gray-500">Cancelled: <span className="font-semibold text-gray-500">{counts.Cancelled}</span></span>
        </div>

        {loading && <div className="text-sm text-gray-500" data-testid="orders-loading">Loading…</div>}

        {!loading && orders.length === 0 && (
          <div className="border border-dashed border-gray-300 rounded-lg p-8 text-center" data-testid="orders-empty">
            <div className="text-sm text-gray-600 mb-2">No {type.toLowerCase()}s yet.</div>
            <Button size="sm" variant="outline" onClick={() => nav(`/orders/new?type=${encodeURIComponent(type)}`)}>
              <Plus className="w-4 h-4 mr-1" /> Create your first {type}
            </Button>
          </div>
        )}

        {!loading && orders.length > 0 && (
          <div className="border border-gray-200 rounded bg-white overflow-hidden">
            <table className="w-full" data-testid="orders-table">
              <thead>
                <tr className="text-[10px] uppercase text-gray-500 border-b border-gray-200 bg-gray-50">
                  <th className="text-left pl-3 py-2 font-medium">Order No</th>
                  <th className="text-left py-2 font-medium">Date</th>
                  <th className="text-left py-2 font-medium">Party</th>
                  <th className="text-left py-2 font-medium">Items</th>
                  <th className="text-right py-2 pr-3 font-medium">Total</th>
                  <th className="text-center py-2 font-medium">Status</th>
                  <th className="text-right py-2 pr-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`order-row-${o.order_no}`}>
                    <td className="pl-3 py-2 font-mono text-xs text-blue-700">{o.order_no}</td>
                    <td className="py-2 text-xs text-gray-700">{o.date}</td>
                    <td className="py-2 text-xs text-gray-900 font-medium">{o.party_name}</td>
                    <td className="py-2 text-xs text-gray-600">
                      {(o.items || []).slice(0, 2).map((it) => `${it.item_name} (${it.qty})`).join(", ")}
                      {(o.items || []).length > 2 && ` +${o.items.length - 2} more`}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono text-xs text-gray-900 font-semibold">{fmt(o.total)}</td>
                    <td className="py-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded border text-[10px] font-semibold ${STATUS_BADGE[o.status] || ""}`}>
                        {o.status}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-right">
                      <div className="inline-flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-[10px] px-2"
                          onClick={() => onPrint(o)}
                          title={
                            o.order_type === "Quotation"
                              ? "Print as Proforma Invoice"
                              : o.order_type === "Delivery Note"
                              ? "Print as Delivery Challan"
                              : "Print"
                          }
                          data-testid={`btn-print-${o.order_no}`}
                        >
                          <Printer className="w-3 h-3" />
                        </Button>
                        {o.status === "Open" && (
                          <>
                            <Button size="sm" variant="outline" className="h-7 text-[10px] px-2"
                                    onClick={() => nav(`/orders/${o.id}/edit`)}
                                    data-testid={`btn-edit-${o.order_no}`}>
                              <Edit2 className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-[10px] px-2 text-red-700 border-red-200 hover:bg-red-50"
                                    onClick={() => deleteOrder(o.id)}
                                    data-testid={`btn-delete-${o.order_no}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                            <Button size="sm" className="h-7 text-[10px] px-2"
                                    onClick={() => convertOrder(o.id)}
                                    data-testid={`btn-convert-${o.order_no}`}>
                              Convert <ArrowRight className="w-3 h-3 ml-0.5" />
                            </Button>
                          </>
                        )}
                        {o.status === "Converted" && o.converted_voucher_id && (
                          <Button size="sm" variant="outline" className="h-7 text-[10px] px-2"
                                  onClick={() => nav(`/vouchers/${o.converted_voucher_id}`)}>
                            View Voucher <ChevronRight className="w-3 h-3 ml-0.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="text-[11px] text-gray-500">
          Orders track <span className="font-semibold">commitments</span> and don&apos;t post to the General Ledger.
          Click <span className="font-semibold">Convert</span> on an Open order to create the linked Sales / Purchase voucher automatically.
        </div>
      </div>
    </>
  );
}

export default function Orders() {
  return <RequireCompany><Inner /></RequireCompany>;
}
